/**
 * Shared, game-agnostic round verifier — the reusable "provably fair model."
 *
 * Any 155 game (crash, marble races, vehicle/duck) reuses this: it checks the
 * per-round commitment and (v2) the hash-chain linkage, and derives the uniform
 * `r`. The game supplies ONLY its r -> result mapping (crash: crashMultiplier;
 * a marble race: an r-stream -> finishing order; etc.) and compares.
 *
 * Spec: marbles-stack-go/docs/provably-fair-standard.md. Mirrors the Python
 * reference; locked to it via fairness-vectors.json.
 */

import * as core from './core'

const R_DENOM = 2 ** 52

export interface RoundProof {
  schemeVersion: number
  serverSeed: string
  /** The commitment observed BEFORE the round (from race_ready). Omitted when
   *  it was not independently observed — then "committed before you bet" cannot
   *  be certified (commitmentVerified stays undefined). */
  observedCommitment?: string
  // v1 (legacy)
  nonce?: string
  // v2 (hash chain)
  beacon?: string // "" for crash; drand randomness for beacon games
  chainRootHash?: string
  chainIndex?: number
}

export interface CommitmentVerdict {
  schemeVersion: number
  /** v1: sha256("seed:nonce"); v2: sha256(seed). */
  recomputedCommitment: string
  /** true = observed pre-round commitment matches; false = mismatch; undefined =
   *  no pre-round commitment observed, so "committed before bet" is unproven. */
  commitmentVerified?: boolean
  /** v2 only: sha256^chainIndex(seed) === chainRootHash. undefined for v1. */
  chainLinksToRoot?: boolean
}

/** Verify the per-round commitment and (v2) the chain linkage. Game-agnostic. */
export async function verifyCommitment(p: RoundProof): Promise<CommitmentVerdict | null> {
  if (!globalThis.crypto?.subtle) return null

  const v2 = p.schemeVersion >= 2
  const recomputedCommitment = v2
    ? await core.sha256Ascii(p.serverSeed) // v2: commitment == the chain link
    : await core.sha256Ascii(`${p.serverSeed}:${p.nonce ?? ''}`) // v1: binds seed AND nonce

  const commitmentVerified =
    p.observedCommitment !== undefined
      ? recomputedCommitment === p.observedCommitment.toLowerCase()
      : undefined

  let chainLinksToRoot: boolean | undefined
  if (v2 && p.chainRootHash && typeof p.chainIndex === 'number') {
    chainLinksToRoot =
      (await core.walkToRoot(p.serverSeed, p.chainIndex)) === p.chainRootHash.toLowerCase()
  }

  return {
    schemeVersion: p.schemeVersion,
    recomputedCommitment,
    commitmentVerified,
    chainLinksToRoot
  }
}

/**
 * Derive the uniform `r` for a game's draw. v2 uses the domain-separated core
 * (`label` selects the draw, e.g. "crash" | "order"); v1 uses HMAC(seed, nonce).
 */
export async function deriveR(p: RoundProof, label: string, index = 0): Promise<number | null> {
  if (!globalThis.crypto?.subtle) return null
  if (p.schemeVersion >= 2) {
    return core.uniform(p.serverSeed, p.beacon ?? '', label, index)
  }
  const hex13 = (await core.hmacSha256Hex(p.serverSeed, p.nonce ?? '')).slice(0, 13)
  return parseInt(hex13, 16) / R_DENOM
}
